import React, { useState, useEffect, useMemo } from "react";
import reports from "../../../../services/reports";
import DashboardHeader from "./DashboardHeader";
import MetricsGrid from "./MetricsGrid";
import NutritionAnalysis from "./NutritionAnalysis";
import ChartsSection from "./ChartsSection";
import MealTypesFilters from "./MealTypesFilters";
import MealTypesList from "./MealTypesList";
import EmptyState from "./EmptyState";
import LoadingSkeleton from "./LoadingSkeleton";
import MealTypeDetailsModal from "./MealTypeDetailsModal";
import { FoodLogStats, MealTypeDetails } from "./types";

interface StudentFoodLogDashboardProps {
  readonly studentId: string | number;
}

const initialStats: FoodLogStats = {
  total_meals: 0,
  total_calories: 0,
  frequency_by_day: [],
  meal_type_stats: [],
  weekday_stats: [],
  adherence_rate: 0,
  days_analyzed: 30,
  days_with_meals: 0,
  avg_daily_calories: 0,
  avg_daily_meals: 0,
  word_cloud_image_url: "",
  nutrition_summary: undefined,
};

export default function StudentFoodLogDashboard({
  studentId,
}: StudentFoodLogDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<number | null>(30);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"count" | "calories" | "name">("count");
  const [selectedMealType, setSelectedMealType] = useState<{
    meal_type: string;
    meal_name: string;
  } | null>(null);
  const [mealDetails, setMealDetails] = useState<MealTypeDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [stats, setStats] = useState<FoodLogStats>(initialStats);

  useEffect(() => {
    async function fetchData() {
      if (!studentId) return;

      setLoading(true);
      try {
        const data = await reports.getStudentFoodLogStats(
          studentId,
          dateFilter || undefined,
        );
        console.log("Estatísticas recebidas:", {
          total_meals: data.total_meals,
          word_cloud_image_url: data.word_cloud_image_url,
        });

        setStats({
          ...data,
          word_cloud_image_url: data.word_cloud_image_url || "",
          nutrition_summary: data.nutrition_summary || undefined,
        });
      } catch (error) {
        console.error("Erro ao buscar estatísticas de alimentação:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [studentId, dateFilter]);

  const handleMealClick = async (meal: {
    meal_type: string;
    meal_name: string;
  }) => {
    setSelectedMealType(meal);
    setLoadingDetails(true);
    try {
      const details = await reports.getMealTypeDetails(
        studentId,
        meal.meal_type,
        dateFilter || undefined,
      );
      setMealDetails(details);
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeModal = () => {
    setSelectedMealType(null);
    setMealDetails(null);
  };

  // Filtrar e ordenar tipos de refeição
  const filteredAndSortedMeals = useMemo(() => {
    let filtered = stats.meal_type_stats.filter((meal) =>
      meal.meal_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Ordenar
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === "count") {
        return b.count - a.count;
      } else if (sortBy === "calories") {
        return b.total_calories - a.total_calories;
      } else {
        return a.meal_name.localeCompare(b.meal_name);
      }
    });

    return filtered;
  }, [stats.meal_type_stats, searchTerm, sortBy]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <DashboardHeader
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
      />

      <MetricsGrid stats={stats} />

      <NutritionAnalysis
        nutritionSummary={stats.nutrition_summary}
        totalMeals={stats.total_meals}
        daysAnalyzed={stats.days_analyzed}
        daysWithMeals={stats.days_with_meals}
        adherenceRate={stats.adherence_rate}
        avgDailyCalories={stats.avg_daily_calories}
        avgDailyMeals={stats.avg_daily_meals}
      />

      <ChartsSection stats={stats} />

      {stats.meal_type_stats.length > 0 && (
        <MealTypesFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      )}

      <MealTypesList
        meals={filteredAndSortedMeals}
        onMealClick={handleMealClick}
      />

      {stats.total_meals === 0 && !loading && <EmptyState />}

      {selectedMealType && (
        <MealTypeDetailsModal
          isOpen={!!selectedMealType}
          onClose={closeModal}
          mealType={selectedMealType}
          details={mealDetails}
          loading={loadingDetails}
        />
      )}
    </div>
  );
}
